import React, { useContext, useState, useEffect } from 'react'
import AppContext from '../utils/AppContext'
import mondaySdk from 'monday-sdk-js'
import backendAPI from '../api/backend'
import IntegrationModal from '../components/IntegrationModal'
import IntegrationSkeleton from '../components/IntegrationSkeleton'
import './IntegrationPage.css'

const monday = mondaySdk()

export default function IntegrationPage({ context, sessionToken }) {
  const { boardId } = context || {}

  const integrationRecipes = [
    {
      id: 1,
      type: 'status_change',
      name: 'Status Change',
      description: 'Send email when column status value changes',
      icon: '🔄'
    },
    {
      id: 2,
      type: 'date_reached',
      name: 'Date Reached',
      description: "Send email when the item's date column reaches today",
      icon: '📅'
    },
    {
      id: 3,
      type: 'person_assigned',
      name: 'Person Assigned',
      description: 'Send email when person is assigned to item',
      icon: '👤'
    }
  ]

  const [integrations, setIntegrations] = useState([])
  const [templates, setTemplates] = useState([])
  const [boardColumns, setBoardColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedRecipe, setSelectedRecipe] = useState(null)
  const [editingIntegration, setEditingIntegration] = useState(null)
const [togglingId, setTogglingId] = useState(null)

  useEffect(() => {
    if (sessionToken) {
      loadData()
    }
  }, [sessionToken])

  async function loadData() {
    try {
      setLoading(true)
      const contextData = await monday.get('context')
      const boardId = contextData.data.boardId

      // Fetch integrations
      const integrationsData = await backendAPI.getIntegrations(boardId, sessionToken)
      setIntegrations(integrationsData.data?.integrations || [])

      // Fetch templates
      const templatesData = await backendAPI.getTemplates(boardId, sessionToken)
      setTemplates(templatesData.data?.templates || [])

      // Fetch board columns from Monday
      const columnsData = await monday.api(`query { boards(ids: [${boardId}]) { columns { id title type settings_str } } }`)
      const columns = columnsData.data.boards[0].columns.map(col => {
        let labels = []
        if (col.settings_str) {
          const settings = JSON.parse(col.settings_str)
          if (settings.labels) {
            labels = Object.values(settings.labels)
          }
        }
        return {
          ...col,
          settings_labels: labels
        }
      })
      setBoardColumns(columns)

      setLoading(false)
    } catch (error) {
      console.error('Failed to load data:', error)
      setLoading(false)
    }
  }

  function handleConfigureRecipe(recipeType) {
    setSelectedRecipe(recipeType)
    setEditingIntegration(null)
    setShowModal(true)
  }

  function handleEditIntegration(integration) {
    setEditingIntegration(integration)
    setSelectedRecipe(integration.recipe_type)
    setShowModal(true)
  }

  async function handleIntegrationSubmit(formData) {
    try {
      const contextData = await monday.get('context')
      const boardId = contextData.data.boardId

      if (editingIntegration) {
        await backendAPI.updateIntegration(editingIntegration.id, formData, sessionToken)
      } else {
        await backendAPI.createIntegration(boardId, formData, sessionToken)
      }

      await loadData()
      setShowModal(false)
      monday.execute('showNotification', {
        text: editingIntegration ? 'Integration updated' : 'Integration created',
        type: 'success'
      })
    } catch (error) {
      console.error('Failed to save integration:', error)
      monday.execute('showNotification', {
        text: 'Failed to save integration',
        type: 'error'
      })
    }
  }

  async function handleDeleteIntegration(integrationId) {
    if (!confirm('Are you sure you want to delete this integration?')) return

    try {
      await backendAPI.deleteIntegration(integrationId, sessionToken)
      await loadData()
      monday.execute('showNotification', {
        text: 'Integration deleted',
        type: 'success'
      })
    } catch (error) {
      console.error('Failed to delete integration:', error)
    }
  }

  async function handleToggleIntegration(integration) {
    const currentEnabled = integration.enabled ?? 1
    const nextEnabled = currentEnabled ? 0 : 1
    setTogglingId(integration.id)
    // Optimistic update — revert on failure.
    setIntegrations(prev => prev.map(i =>
      i.id === integration.id ? { ...i, enabled: nextEnabled } : i
    ))

    try {
      await backendAPI.toggleIntegration(integration.id, Boolean(nextEnabled), sessionToken)
      monday.execute('showNotification', {
        text: nextEnabled ? 'Automation enabled' : 'Automation paused',
        type: nextEnabled ? 'success' : 'info'
      })
    } catch (error) {
      console.error('Failed to toggle integration:', error)
      setIntegrations(prev => prev.map(i =>
        i.id === integration.id ? { ...i, enabled: currentEnabled } : i
      ))
      monday.execute('showNotification', {
        text: 'Failed to update automation',
        type: 'error'
      })
    } finally {
      setTogglingId(null)
    }
  }

  function isEnabled(integration) {
    return (integration.enabled ?? 1) === 1
  }

  function getColumnById(columnId) {
    return boardColumns.find((column) => column.id === columnId)
  }

  function getColumnTitle(columnId) {
    return getColumnById(columnId)?.title || columnId || 'Unknown column'
  }

  function getRecipeLabel(recipeType) {
    const recipe = integrationRecipes.find((item) => item.type === recipeType)
    return recipe?.name || recipeType.replace(/_/g, ' ')
  }

  function parseRecipientColumns(value) {
    if (!value) return []

    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  function formatRecipientLabel(column) {
    if (!column) return null

    if (column.type === 'mirror') {
      return `${column.title} (Mirror)`
    }

    if (column.type === 'text') {
      return `${column.title} (text column)`
    }

    return column.title
  }

  function formatRecipientList(integration) {
    const columns = parseRecipientColumns(integration.recipient_columns)
      .map((columnId) => getColumnById(columnId))
      .filter(Boolean)
      .map(formatRecipientLabel)
      .filter(Boolean)

    if (!columns.length) {
      return 'recipients'
    }

    if (columns.length === 1) {
      return columns[0]
    }

    if (columns.length === 2) {
      return `${columns[0]} and ${columns[1]}`
    }

    return `${columns.slice(0, -1).join(', ')} and ${columns[columns.length - 1]}`
  }

  function buildIntegrationSummary(integration) {
    const templateName = integration.template_name || 'Template'
    const recipientLabel = formatRecipientList(integration)
    const ccSuffix = integration.cc_enabled ? ' and cc' : ''

    if (integration.recipe_type === 'status_change') {
      const statusColumn = getColumnTitle(integration.trigger_column)
      const triggerValue = integration.trigger_value || 'something'
      return `When ${statusColumn} changes to ${triggerValue} send ${templateName} to ${recipientLabel}${ccSuffix}`
    }

    if (integration.recipe_type === 'date_reached') {
      const dateColumn = getColumnTitle(integration.trigger_column)
      return `When ${dateColumn} arrives send ${templateName} to ${recipientLabel}${ccSuffix}`
    }

    if (integration.recipe_type === 'person_assigned') {
      const personColumn = getColumnTitle(integration.trigger_column)
      return `When ${personColumn} is assigned send ${templateName} to ${recipientLabel}${ccSuffix}`
    }

    return `When ${getRecipeLabel(integration.recipe_type)} triggers send ${templateName} to ${recipientLabel}${ccSuffix}`
  }

  return (
    <div className="page-container integration-page">
      <div className="page-header">
        <h2>⚡ Automations</h2>
        <p>Configure automatic email sending workflows</p>
      </div>

      {loading ? (
        <IntegrationSkeleton />
      ) : (
        <>
          <div className="integration-recipes">
            {integrationRecipes.map((recipe) => (
              <div key={recipe.id} className="recipe-card">
                <div className="recipe-icon">{recipe.icon}</div>
                <div className="recipe-content">
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
                <button className="recipe-btn" onClick={() => handleConfigureRecipe(recipe.type)}>Configure</button>
              </div>
            ))}
          </div>

          {integrations && integrations.length > 0 && (
            <div className="active-integrations-section">
              <div className="page-header" style={{ marginTop: '48px' }}>
                <h2>✅ Active Automations</h2>
                <p>Manage your existing email workflows</p>
              </div>
              <div className="active-integrations-list">
                {integrations.map((integration) => (
                  <div key={integration.id}
                       className="active-integration-item"
                       style={{ opacity: isEnabled(integration) ? 1 : 0.6 }}>
                    <div className="integration-info">
                      <div className="integration-type-badge">
                        {integrationRecipes.find(r => r.type === integration.recipe_type)?.icon || '⚡'}
                      </div>
                      <div className="integration-details">
                        <h3>
                          {integration.template_name || 'Email Automation'}
                          {!isEnabled(integration) && (
                            <span style={{
                              marginLeft: 8,
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#9699a6',
                              backgroundColor: '#f0f0f3',
                              padding: '2px 8px',
                              borderRadius: 10
                            }}>Paused</span>
                          )}
                        </h3>
                        <p>{buildIntegrationSummary(integration)}</p>
                        <div className="integration-meta">
                          <span>{getRecipeLabel(integration.recipe_type)}</span>
                          {integration.trigger_column && <span>{getColumnTitle(integration.trigger_column)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="integration-actions" style={{ alignItems: 'center' }}>
                      <div
                        onClick={() => handleToggleIntegration(integration)}
                        title={isEnabled(integration) ? 'Click to pause' : 'Click to enable'}
                        style={{
                          width: 36,
                          height: 20,
                          backgroundColor: isEnabled(integration) ? '#0073ea' : '#c9c9c9',
                          borderRadius: 10,
                          position: 'relative',
                          cursor: togglingId === integration.id ? 'wait' : 'pointer',
                          opacity: togglingId === integration.id ? 0.6 : 1,
                          transition: 'background-color 0.2s',
                          flexShrink: 0
                        }}
                      >
                        <div style={{
                          width: 16,
                          height: 16,
                          backgroundColor: '#ffffff',
                          borderRadius: '50%',
                          position: 'absolute',
                          top: 2,
                          ...(isEnabled(integration) ? { right: 2 } : { left: 2 }),
                          transition: 'left 0.2s, right 0.2s'
                        }} />
                      </div>
                      <button className="btn-edit" onClick={() => handleEditIntegration(integration)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDeleteIntegration(integration.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {showModal && (
        <IntegrationModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          recipeType={selectedRecipe}
          templates={templates}
          boardColumns={boardColumns}
          integration={editingIntegration}
          onSubmit={handleIntegrationSubmit}
        />
      )}
    </div>
  )
}
